import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ImagePlus, Loader2, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const TERRACOTTA = "hsl(13 74% 54%)";

// Fit any logo into a white square so it sits cleanly in the QR centre.
const squareLogo = (file: File): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const S = 256, pad = 16, c = document.createElement("canvas");
      c.width = c.height = S;
      const ctx = c.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, S, S);
      const k = Math.min((S - pad * 2) / img.width, (S - pad * 2) / img.height);
      const w = img.width * k, h = img.height * k;
      ctx.drawImage(img, (S - w) / 2, (S - h) / 2, w, h);
      c.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read image"))), "image/png");
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error("Could not read image"));
    img.src = URL.createObjectURL(file);
  });

const toDataUrl = async (url: string) => {
  const blob = await (await fetch(url)).blob();
  return new Promise<string>((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result as string); fr.readAsDataURL(blob); });
};

function printHtml(svg: string, cta: string, layout: Layout) {
  const esc = (s: string) => s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
  const footer = `<p>Powered by <b>ExpoEvent<span style="color:${TERRACOTTA}">HUB</span>.com</b></p>`;
  if (layout === "tent") {
    const panel = (flip: boolean) => `<section class="panel${flip ? " flip" : ""}"><div class="row">${cta ? `<h1>${esc(cta)}</h1>` : ""}<div class="qr">${svg}</div></div>${footer}</section>`;
    return `<!doctype html><html><head><title>Booth QR tent</title><style>
@page{size:letter portrait;margin:0}html,body{margin:0;padding:0}
body{width:8.5in;height:11in;font-family:Helvetica,Arial,sans-serif;color:#1C1917;position:relative}
.panel{box-sizing:border-box;width:8.5in;height:5.5in;padding:0.45in 0.5in;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.2in}
.flip{transform:rotate(180deg)}.row{display:flex;align-items:center;justify-content:center;gap:0.4in;width:100%}
h1{flex:1;max-width:3.6in;font-size:24pt;line-height:1.15;margin:0;font-weight:800;text-align:left}
.qr svg{width:3.5in;height:3.5in;display:block}p{font-size:11pt;margin:0;text-align:center}
.fold{position:absolute;left:0;right:0;top:5.5in;border-top:1px dashed #A8A29E}
.fold span{position:absolute;right:0.3in;top:-0.16in;font-size:7pt;color:#A8A29E;background:#fff;padding:0 4px}
</style></head><body>${panel(true)}<div class="fold"><span>Fold here</span></div>${panel(false)}</body></html>`;
  }
  return `<!doctype html><html><head><title>Booth QR</title><style>
@page{size:letter;margin:0.5in}html,body{margin:0;height:100%}
body{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.35in;font-family:Helvetica,Arial,sans-serif;color:#1C1917;text-align:center}
h1{font-size:32pt;line-height:1.15;margin:0;max-width:7in;font-weight:800}
.qr svg{width:5in;height:5in;display:block}p{font-size:12pt;margin:0}
</style></head><body>${cta ? `<h1>${esc(cta)}</h1>` : ""}<div class="qr">${svg}</div>
${footer}</body></html>`;
}

type Layout = "standard" | "tent";

const Footer = ({ className = "" }: { className?: string }) => (
  <p className={className}>Powered by <b>ExpoEvent<span style={{ color: TERRACOTTA }}>HUB</span>.com</b></p>
);

function TentPanel({ cta, qr, flip }: { cta: string; qr: React.ReactNode; flip?: boolean }) {
  return (
    <div className={`flex h-1/2 flex-col items-center justify-center gap-1.5 px-3 ${flip ? "rotate-180" : ""}`}>
      <div className="flex w-full items-center justify-center gap-3">
        {cta.trim() && <p className="max-w-[45%] flex-1 text-left text-sm font-extrabold leading-tight">{cta}</p>}
        <div className="aspect-square w-[42%] [&>svg]:h-full [&>svg]:w-full">{qr}</div>
      </div>
      <Footer className="text-[9px]" />
    </div>
  );
}

export function QrPrintDialog({ event, url, onClose, onChanged }: { event: any; url: string; onClose: () => void; onChanged: () => void }) {
  const [cta, setCta] = useState(event.print_cta || "");
  const [logo, setLogo] = useState<string | null>(null);
  const [busy, setBusy] = useState("");
  const [layout, setLayout] = useState<Layout>("standard");
  const qrRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const src = fileUrl(event.print_logo_path);
    if (src) toDataUrl(src).then(setLogo).catch(() => setLogo(null));
  }, [event.print_logo_path]);

  const upload = async (f?: File) => {
    if (!f) return;
    setBusy("logo");
    try {
      const fd = new FormData();
      fd.append("file", await squareLogo(f), "qr-logo.png");
      await api.post(`/vendor/events/${event.id}/print-logo`, fd);
      onChanged();
      toast.success("Logo added to the QR code");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const removeLogo = async () => {
    try { await api.delete(`/vendor/events/${event.id}/print-logo`); setLogo(null); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };

  const print = async () => {
    setBusy("print");
    try {
      if (cta !== (event.print_cta || "")) { await api.put(`/vendor/events/${event.id}/print`, { cta }); onChanged(); }
      const svg = qrRef.current?.querySelector("svg")?.outerHTML || "";
      const frame = document.createElement("iframe");
      frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
      document.body.appendChild(frame);
      const doc = frame.contentWindow!.document;
      doc.open(); doc.write(printHtml(svg, cta.trim(), layout)); doc.close();
      setTimeout(() => {
        frame.contentWindow!.focus();
        frame.contentWindow!.print();
        setTimeout(() => frame.remove(), 1000);
      }, 300);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const code = (
    <QRCodeSVG value={url} size={480} level="H" fgColor="#1C1917" marginSize={2}
      imageSettings={logo ? { src: logo, height: 106, width: 106, excavate: true } : undefined} />
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="qr-print-dialog">
        <DialogHeader><DialogTitle>Print booth QR · {event.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Call to action (printed above the QR)</Label>
            <Input value={cta} maxLength={120} placeholder="e.g. Scan to get our wedding price guide" onChange={(e) => setCta(e.target.value)} data-testid="qr-print-cta-input" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="qr-print-logo-file" />
            <Button size="sm" variant="secondary" disabled={busy === "logo"} onClick={() => fileRef.current?.click()} data-testid="qr-print-logo-upload-button">
              {busy === "logo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="mr-1 h-4 w-4" /> {logo ? "Change logo" : "Add logo to QR"}</>}
            </Button>
            {logo && <Button size="sm" variant="ghost" className="text-rose-500" onClick={removeLogo} data-testid="qr-print-logo-remove-button"><Trash2 className="mr-1 h-4 w-4" /> Remove logo</Button>}
          </div>
        </div>
        <RadioGroup value={layout} onValueChange={(v) => setLayout(v as Layout)} className="grid grid-cols-2 gap-2" data-testid="qr-print-layout">
          {([["standard", "Standard page", "5 in QR on a full page"], ["tent", "Table tent", "Folds to stand on a table"]] as const).map(([k, label, hint]) => (
            <label key={k} className={`flex cursor-pointer items-start gap-2 rounded-sm border p-3 text-sm ${layout === k ? "border-primary bg-primary/5" : ""}`}>
              <RadioGroupItem value={k} className="mt-0.5" data-testid={`qr-print-layout-${k}`} />
              <span><span className="block font-semibold">{label}</span><span className="block text-xs text-muted-foreground">{hint}</span></span>
            </label>
          ))}
        </RadioGroup>
        <div ref={qrRef} className="hidden">{code}</div>
        {layout === "standard" ? (
          <div className="mx-auto w-full max-w-[340px] rounded-sm border bg-white p-5 text-center text-stone-900 shadow-sm" data-testid="qr-print-preview">
            {cta.trim() && <p className="mb-3 text-xl font-extrabold leading-tight" data-testid="qr-print-preview-cta">{cta}</p>}
            <div className="mx-auto aspect-square w-full [&>svg]:h-full [&>svg]:w-full" data-testid="qr-print-preview-code">{code}</div>
            <Footer className="mt-3 text-sm" />
          </div>
        ) : (
          <div className="relative mx-auto aspect-[8.5/11] w-full max-w-[300px] rounded-sm border bg-white text-stone-900 shadow-sm" data-testid="qr-print-tent-preview">
            <TentPanel cta={cta} qr={code} flip />
            <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-stone-400"><span className="absolute -top-2 right-2 bg-white px-1 text-[8px] text-stone-400">Fold here</span></div>
            <TentPanel cta={cta} qr={code} />
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground">{layout === "standard" ? "Prints at about 5 × 5 inches, centered on the page." : "Letter page, folded along the dashed line into two 8.5 × 5.5 in sides with a 3.5 in QR on each."}</p>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="qr-print-cancel-button">Cancel</Button>
          <Button onClick={print} disabled={busy === "print"} className="bg-primary text-white hover:bg-primary/90" data-testid="qr-print-confirm-button"><Printer className="mr-1 h-4 w-4" /> Print</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
